import { isNil } from '@gfe/core/utils/shared-utils';

import { toScreamingSnakeCase, toSnakeCase } from '@core/utils/case-conversions';
import {
  PropertyInformation,
  getJsonTypeOfTypesList,
  isDurationProperty,
  resolveAllPropertyInformation
} from '@core/utils/json-schema-utils';

import {
  CustomValueConfig,
  CustomValueStrategy,
  FieldCustomMap,
  FieldItems,
  FieldList,
  FieldTaggedSection,
  FormBuilderBase,
  FormBuilderFieldType,
  FormBuilderItem,
  FormBuilderSchema,
  FormBuilderSection
} from '@shared/components/row-edition-form/form-builder/form-builder.model';

import { JsonSchema, TransformerInfo } from '../../transformers.model';
import { TransformerInterface } from '../entity-visualization-interface';

// WeakMap stores the translate function during formBuilderSchema() execution without
// mutating the frozen NgRx state object.
const translateFnRegistry = new WeakMap<GenericCsvTransformer, (key: string) => string>();

/**
 * Site schedule compatibility lists. Their schema describes an empty list as "no
 * restriction", but the backend's compatibility check matches `[]` against nothing; only
 * `null` leaves the schedule unrestricted. Opt-in by name because elsewhere `[]` and `null`
 * can legitimately differ.
 */
const NULL_WHEN_EMPTIED_LISTS: ReadonlySet<string> = new Set([
  'compatible_commodity_uids',
  'compatible_job_kind_uids',
  'compatible_flow_kind_uids',
  'compatible_pickup_site_uids',
  'compatible_delivery_site_uids'
]);

export class GenericCsvTransformer implements TransformerInterface {
  constructor(public transformerInfo: TransformerInfo) {}

  private rootSchema(): JsonSchema {
    return this.transformerInfo.dataModels[0].metadata.schema;
  }

  formBuilderSchema(
    ignoreIdentifier: boolean = false,
    translateFn?: (key: string) => string
  ): FormBuilderSchema {
    if (translateFn) translateFnRegistry.set(this, translateFn);
    try {
      const schema = this.rootSchema();
      const properties = schema.properties;
      const required = schema.required ?? [];

      if (isNil(properties)) {
        return { items: [] };
      }

      const items = Object.keys(properties)
        .filter((prop) => !(ignoreIdentifier && prop === 'uid'))
        .map((prop) =>
          this.isCompatibilityCfg(prop, schema)
            ? this.createCompatibilityCfgField(
                prop,
                properties[prop],
                required.includes(prop)
              )
            : this.processProperty(prop, properties[prop], required.includes(prop))
        )
        .filter(Boolean);

      // Move uid to the first position making it to be showed at the top of the form
      if (!ignoreIdentifier) {
        const uidIndex = items.findIndex((item) => item.id === 'uid');
        if (uidIndex !== -1) {
          items.unshift(items.splice(uidIndex, 1)[0]);
        }
      }

      return { items };
    } finally {
      translateFnRegistry.delete(this);
    }
  }

  /**
   * A carrier's `compatibility_cfg` is edited as requirement cards. Its schema is the recursive
   * filter grammar, which the generic walk can only flatten, and the editor needs the carrier's
   * `x-compatibility-*` extensions, which only the root schema carries — so it is recognised here,
   * at the top level, rather than in `processProperty`.
   */
  private isCompatibilityCfg(propName: string, schema: JsonSchema): boolean {
    return (
      propName === 'compatibility_cfg' && schema['x-compatibility-fields'] !== undefined
    );
  }

  private createCompatibilityCfgField(
    propName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    propertySchema: any,
    isRequired: boolean
  ): FormBuilderItem {
    return {
      ...this.getBaseConfig(
        propName,
        resolveAllPropertyInformation(propertySchema, this.rootSchema()),
        isRequired
      ),
      isArray: false,
      type: FormBuilderFieldType.COMPATIBILITY_CFG,
      carrierSchema: this.rootSchema()
    } as FormBuilderItem;
  }

  private processProperty(
    propName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    propertySchema: any,
    isRequired: boolean,
    canHaveMultipleField: boolean = true,
    visitedRefs: ReadonlySet<string> = new Set<string>()
  ): FormBuilderItem {
    const propInfo = resolveAllPropertyInformation(propertySchema, this.rootSchema());

    // Recursive-schema guard. `resolveAllRefs` stops inlining a self-referential `$ref`
    // (e.g. the data-layer `CompatibilityFilter`), but the transformer re-resolves a fresh
    // ref one level deeper on every descent — so without this guard a self-referential
    // schema would still recurse forever here. `x-ref-type` carries the originating ref;
    // once it is already being expanded on the current path we stop descending and render
    // a terminal field, so the rest of the entity form still builds.
    const refType = propInfo.data?.['x-ref-type'];
    if (typeof refType === 'string' && visitedRefs.has(refType)) {
      return this.createSimpleField(propName, propInfo, isRequired);
    }
    const nextVisited =
      typeof refType === 'string' ? new Set(visitedRefs).add(refType) : visitedRefs;

    if (this.isGeoJsonPolygon(propName, propInfo)) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.GEOJSON_POLYGON
      } as FormBuilderItem;
    }

    if (this.isPolyline(propName, propInfo)) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.POLYLINE
      } as FormBuilderItem;
    }

    if (this.isColor(propName, propInfo)) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.COLOR
      } as FormBuilderItem;
    }

    if (
      propName === 'tags' &&
      propInfo.types.includes('array') &&
      propInfo.data?.['items'] &&
      typeof propInfo.data['items'] === 'object' &&
      (propInfo.data['items']['type'] === 'string' ||
        (Array.isArray(propInfo.data['items']['type']) &&
          propInfo.data['items']['type'].includes('string'))) &&
      !propInfo.data['items']['enum'] &&
      !propInfo.data['items']['x-reference']
    ) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.TAGS_INPUT
      } as FormBuilderItem;
    }

    // A property named `labels` whose schema is an open key/value map (additionalProperties,
    // no fixed properties) is the entity labels map. Value types are derived per-label from the
    // labels store at render time, so we accept any additionalProperties value shape here —
    // string, number/boolean, or a string|number|bool union ($ref / oneOf / anyOf).
    if (propName === 'labels' && this.hasAdditionalPropertiesOnly(propInfo.data)) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.LABELS_MAP
      } as FormBuilderItem;
    }

    if (this.hasOneOfOrAnyOf(propInfo.data)) {
      return this.createChoiceField(propName, propInfo, isRequired, nextVisited);
    }

    if (propInfo.data?.['enum']) {
      if (this.isNullable(propInfo)) {
        const itemField = this.createItemField(propName, propInfo, isRequired);
        return {
          ...this.getBaseConfig(propName, propInfo, isRequired),
          isArray: false,
          type: FormBuilderFieldType.MULTIPLAY,
          oneOf: [itemField, { type: FormBuilderFieldType.CONST, const: null }]
        };
      }
      return this.createItemField(propName, propInfo, isRequired);
    }

    if (this.hasAdditionalPropertiesOnly(propInfo.data)) {
      return this.createCustomMapField(propName, propInfo, isRequired);
    }

    if (propInfo.types.includes('array') && propInfo.data?.['items']) {
      return this.processArrayProperty(propName, propInfo, isRequired, nextVisited);
    }

    if (
      propInfo.containMultipleField &&
      propInfo.data?.['properties'] &&
      canHaveMultipleField
    ) {
      return this.createSection(propName, propInfo, isRequired, nextVisited);
    }

    // x-immutable on the entity's own uid field: render as free-text STRING, not entity selector.
    // Other x-reference fields (e.g. site_uid) may also be immutable but still need AUTO_COMPLETE.
    if (
      propName === 'uid' &&
      propInfo.data?.['x-immutable'] &&
      propInfo.data?.['x-reference']
    ) {
      return this.createOwnUidField(propName, propInfo, isRequired);
    }

    const field = this.createSimpleField(propName, propInfo, isRequired);
    // The duration control has no empty state — it shows 0 seconds for `null` — so a
    // nullable duration gets an explicit None variant. Text, number, date and reference
    // controls show `null` as an empty input and need none.
    if (
      this.isNullable(propInfo) &&
      'type' in field &&
      field.type === FormBuilderFieldType.DURATION
    ) {
      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        isArray: false,
        type: FormBuilderFieldType.MULTIPLAY,
        nullableDuration: true,
        oneOf: [field as FieldList, { type: FormBuilderFieldType.CONST, const: null }]
      };
    }
    return field;
  }

  /**
   * `simplifyAnyOf` encodes a nullable node two ways: a `$ref` variant (e.g. an optional
   * `Iso8601Duration`) gets `nullable: true`, an inline type gets `'null'` in its types.
   */
  private isNullable(propInfo: PropertyInformation): boolean {
    return propInfo.types.includes('null') || !!propInfo.data?.['nullable'];
  }

  private createOwnUidField(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean
  ): FormBuilderItem {
    const isNullable = propInfo.types.includes('null');

    const stringSubItem = {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      isArray: false,
      type: FormBuilderFieldType.STRING,
      pattern: propInfo.data?.['pattern'],
      maxLength: propInfo.data?.['maxLength'],
      minLength: propInfo.data?.['minLength']
    };

    if (!isNullable) {
      return stringSubItem as FormBuilderItem;
    }

    return {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      isArray: false,
      type: FormBuilderFieldType.MULTIPLAY,
      oneOf: [{ type: FormBuilderFieldType.CONST, const: null }, stringSubItem]
    } as FormBuilderItem;
  }

  /**
   * `x-display-type: "geojson-polygon"` is the intended signal, but the data layer holds a
   * geometry as an untyped JSON value, for which its schema generator emits the bare `true`
   * schema — a boolean node cannot carry an extension keyword. A `geometry` property that
   * constrains nothing is therefore recognised by name; one with a real schema still needs
   * the annotation.
   */
  private isGeoJsonPolygon(propName: string, propInfo: PropertyInformation): boolean {
    if (propInfo.data?.['x-display-type'] === 'geojson-polygon') {
      return true;
    }
    return propName === 'geometry' && this.isUnconstrainedSchema(propInfo.data);
  }

  /**
   * `x-display-type: "polyline"` is the intended signal. The data-layer annotation is not
   * shipped yet, so any string property named `polyline` is also recognised. That fallback
   * is deliberately looser than the one `geometry` uses, which additionally requires an
   * unconstrained schema: the polyline node is a plain `Option<String>` and may gain
   * length or pattern constraints without ceasing to be a polyline, and a stricter rule
   * would fail silently when it did. The cost is that another entity's string property of
   * the same name would be swallowed, which is why the annotation supersedes this.
   * `processProperty` sees one property at a time and cannot consult `polyline_format`.
   */
  private isPolyline(propName: string, propInfo: PropertyInformation): boolean {
    if (propInfo.data?.['x-display-type'] === 'polyline') {
      return true;
    }
    return propName === 'polyline' && propInfo.types.includes('string');
  }

  /**
   * `x-display-type: "color"` is the intended signal. The data layer declares its color
   * properties (site, route) as plain hex-patterned strings with no `format`, and the two
   * patterns differ (`#RGB`-or-`#RRGGBB` versus `#RRGGBB` only), so a string property
   * named `color` is recognised by name rather than by matching a pattern.
   */
  private isColor(propName: string, propInfo: PropertyInformation): boolean {
    if (propInfo.data?.['x-display-type'] === 'color') {
      return true;
    }
    return propName === 'color' && propInfo.types.includes('string');
  }

  /** A schema that accepts any value: `true`, or an object holding annotations only. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isUnconstrainedSchema(data: any): boolean {
    if (data === true) {
      return true;
    }
    if (!this.isSchemaObject(data)) {
      return false;
    }
    return Object.keys(data).every(
      (key) => key.startsWith('x-') || ['title', 'description', 'default'].includes(key)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isSchemaObject(value: any): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private hasOneOfOrAnyOf(data: any): boolean {
    return data && (Array.isArray(data.oneOf) || Array.isArray(data.anyOf));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private hasAdditionalPropertiesOnly(data: any): boolean {
    if (!data || typeof data !== 'object') return false;
    const hasAdditionalProps = data.additionalProperties !== undefined;
    const hasNoProperties = !data.properties || Object.keys(data.properties).length === 0;
    const isObjectType =
      data.type === 'object' || (Array.isArray(data.type) && data.type.includes('object'));
    return hasAdditionalProps && hasNoProperties && isObjectType;
  }

  private resolveLabelFallback(propName: string, _lang: string): string {
    const translateFn = translateFnRegistry.get(this);
    if (!translateFn) return propName;
    const key = `table.columns.${propName}`;
    const translated = translateFn(key);
    return translated === key ? propName : translated;
  }

  private getBaseConfig(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean
  ) {
    return {
      required: isRequired,
      id: propName,
      label: {
        fr:
          propInfo.data?.['x-i18n-title']?.['fr'] ??
          this.resolveLabelFallback(propName, 'fr'),
        en:
          propInfo.data?.['x-i18n-title']?.['en'] ??
          propInfo.data?.['title'] ??
          this.resolveLabelFallback(propName, 'en'),
        es:
          propInfo.data?.['x-i18n-title']?.['es'] ??
          this.resolveLabelFallback(propName, 'es'),
        'pt-BR':
          propInfo.data?.['x-i18n-title']?.['pt-BR'] ??
          this.resolveLabelFallback(propName, 'pt-BR')
      },
      description: {
        fr: propInfo.data?.['x-i18n-description']?.['fr'] ?? '',
        en: propInfo.data?.['x-i18n-description']?.['en'] ?? propInfo.data?.['description'],
        es: propInfo.data?.['x-i18n-description']?.['es'] ?? '',
        'pt-BR': propInfo.data?.['x-i18n-description']?.['pt-BR'] ?? ''
      },
      fieldOrder: propInfo.data?.['x-field-order'] ?? 9999,
      immutable: propInfo.data?.['x-immutable'] === true ? true : undefined
    };
  }

  private createItemField(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean
  ): FieldItems & FormBuilderBase {
    const enumeration = propInfo.data?.['enum'] || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const values = enumeration.map((val: any) => ({
      label: String(val),
      value: val
    }));

    return {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      isArray: false,
      type: FormBuilderFieldType.ITEMS,
      values
    };
  }

  private createCustomMapField(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean
  ): FieldCustomMap & FormBuilderBase {
    const valueTypes = this.extractValueTypes(propInfo.data?.['additionalProperties']);
    return {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      isArray: false,
      type: FormBuilderFieldType.CUSTOM_MAP,
      valueTypes
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractValueTypes(additionalProps: any): ('string' | 'number' | 'boolean')[] {
    if (!additionalProps) return ['string', 'number', 'boolean'];

    // Handle $ref patterns like { "$ref": "#/$defs/UserBoolStringNumber" }
    if (additionalProps.$ref) {
      const refName = additionalProps.$ref.split('/').pop()?.toLowerCase() ?? '';
      const types: ('string' | 'number' | 'boolean')[] = [];
      if (refName.includes('string')) types.push('string');
      if (refName.includes('number') || refName.includes('int')) types.push('number');
      if (refName.includes('bool')) types.push('boolean');
      return types.length > 0 ? types : ['string', 'number', 'boolean'];
    }

    // Handle oneOf/anyOf patterns
    if (Array.isArray(additionalProps.oneOf) || Array.isArray(additionalProps.anyOf)) {
      const choices = additionalProps.oneOf || additionalProps.anyOf;
      const types = new Set<'string' | 'number' | 'boolean'>();
      for (const choice of choices) {
        if (choice.type === 'string') types.add('string');
        if (choice.type === 'number' || choice.type === 'integer') types.add('number');
        if (choice.type === 'boolean') types.add('boolean');
      }
      return types.size > 0 ? Array.from(types) : ['string', 'number', 'boolean'];
    }

    // Handle direct type specification
    const type = additionalProps.type;
    if (type === 'string') return ['string'];
    if (type === 'number' || type === 'integer') return ['number'];
    if (type === 'boolean') return ['boolean'];

    return ['string', 'number', 'boolean'];
  }

  private createChoiceField(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean,
    visitedRefs: ReadonlySet<string> = new Set<string>()
  ): FormBuilderItem {
    const choices = propInfo.data?.['oneOf'] || propInfo.data?.['anyOf'] || [];
    const fields: FieldList[] = choices
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((choice: any) => {
        if (Array.isArray(choice.enum)) {
          return this.createItemField(
            propName,
            resolveAllPropertyInformation(choice, this.rootSchema()),
            isRequired
          );
        }
        if (typeof choice === 'object' && 'const' in choice) {
          return { type: FormBuilderFieldType.CONST, const: choice.const };
        }
        if (choice.properties) {
          // Check for discriminated union variant (has a const-valued discriminator property)
          const taggedSection = this.tryCreateTaggedSection(choice, visitedRefs);
          if (taggedSection) {
            return taggedSection;
          }
          const propKeys = Object.keys(choice.properties);
          if (propKeys.length === 1) {
            const innerField = this.processProperty(
              propKeys[0],
              choice.properties[propKeys[0]],
              isRequired,
              false,
              visitedRefs
            ) as FieldList;
            // Externally-tagged enum variant (e.g. `{ job_visit_patterns: [...] }`):
            // the wrapper carries the human-readable name/description ("Visit Patterns"),
            // while the single inner property is just the tag. Collapsing into the inner
            // property would otherwise drop that label, leaving the raw key on the form.
            return this.applyWrapperLabel(innerField, choice);
          }
        }
        if (typeof choice === 'object') {
          const propKeys = Object.keys(choice);
          if (propKeys.length < 3 && propKeys.length > 0) {
            return this.processProperty(
              propName,
              choice,
              isRequired,
              false,
              visitedRefs
            ) as FieldList;
          }
        }
        return null;
      })
      .filter(Boolean);

    if (propInfo.data?.['nullable']) {
      fields.push({ type: FormBuilderFieldType.CONST, const: null });
    }

    return {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      isArray: false,
      type: FormBuilderFieldType.MULTIPLAY,
      oneOf: fields
    };
  }

  /**
   * Overlays a collapsed single-property variant with the wrapper choice's own
   * title/description so externally-tagged enum variants keep their display name
   * instead of falling back to the raw inner property key. Per language, the
   * wrapper's `x-i18n-title`/`title` wins when present; otherwise the inner
   * field's existing label is preserved (so anonymous wrappers are untouched).
   */
  private applyWrapperLabel(
    field: FieldList,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    choice: any
  ): FieldList {
    const i18nTitle = choice?.['x-i18n-title'];
    const title = choice?.['title'];
    const i18nDescription = choice?.['x-i18n-description'];
    const description = choice?.['description'];

    if (!i18nTitle && !title && !i18nDescription && !description) {
      return field;
    }

    // The collapsed field is a full FormBuilderItem at runtime (it carries
    // label/description from FormBuilderBase), even though createChoiceField
    // tracks variants as the narrower FieldList.
    const base = field as unknown as FormBuilderItem;
    const innerLabel = base.label;
    const innerDescription = base.description;

    const merged: FormBuilderItem = {
      ...base,
      label: {
        en: i18nTitle?.['en'] ?? title ?? innerLabel?.en,
        fr: i18nTitle?.['fr'] ?? innerLabel?.fr,
        es: i18nTitle?.['es'] ?? innerLabel?.es,
        'pt-BR': i18nTitle?.['pt-BR'] ?? innerLabel?.['pt-BR']
      },
      description: {
        en: i18nDescription?.['en'] ?? description ?? innerDescription?.en ?? '',
        fr: i18nDescription?.['fr'] ?? innerDescription?.fr ?? '',
        es: i18nDescription?.['es'] ?? innerDescription?.es ?? '',
        'pt-BR': i18nDescription?.['pt-BR'] ?? innerDescription?.['pt-BR'] ?? ''
      }
    };

    return merged as unknown as FieldList;
  }

  private tryCreateTaggedSection(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    choice: any,
    visitedRefs: ReadonlySet<string> = new Set<string>()
  ): FieldTaggedSection | null {
    const props = choice.properties;
    const propKeys = Object.keys(props);

    // Find the discriminator: a property with a const value. A property can be a boolean
    // schema (`true`/`false`), which carries no keywords to inspect.
    const discriminatorKey = propKeys.find(
      (key) => this.isSchemaObject(props[key]) && 'const' in props[key]
    );
    if (!discriminatorKey) {
      return null;
    }

    const discriminatorValue = String(props[discriminatorKey].const);
    const subRequired = choice.required ?? [];

    // Process non-discriminator properties as sub-fields
    const fields = propKeys
      .filter((key) => key !== discriminatorKey)
      .map((key) =>
        this.processProperty(key, props[key], subRequired.includes(key), true, visitedRefs)
      )
      .filter(Boolean);

    return {
      type: FormBuilderFieldType.TAGGED_SECTION,
      discriminatorField: discriminatorKey,
      discriminatorValue,
      fields
    };
  }

  private processArrayProperty(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean,
    visitedRefs: ReadonlySet<string> = new Set<string>()
  ): FormBuilderItem {
    const itemsSchema = propInfo.data?.['items'];
    const itemsInfo = resolveAllPropertyInformation(itemsSchema, this.rootSchema());

    if (itemsInfo.containMultipleField && itemsInfo.data?.['properties']) {
      const subRequired = itemsInfo.data['required'] ?? [];
      const items = Object.entries(itemsInfo.data['properties'])
        .map(([subProp, subSchema]) =>
          this.processProperty(
            subProp,
            subSchema,
            subRequired.includes(subProp),
            true,
            visitedRefs
          )
        )
        .filter(Boolean);

      return {
        ...this.getBaseConfig(propName, propInfo, isRequired),
        items,
        isArray: true
      };
    }

    return this.createSimpleField(propName, propInfo, isRequired, true);
  }

  private createSection(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean = false,
    visitedRefs: ReadonlySet<string> = new Set<string>()
  ): FormBuilderSection {
    const subRequired = propInfo.data?.['required'] ?? [];
    const items = Object.entries(propInfo.data?.['properties'])
      .map(([subProp, subSchema]) =>
        this.processProperty(
          subProp,
          subSchema,
          subRequired.includes(subProp),
          true,
          visitedRefs
        )
      )
      .filter(Boolean);

    return {
      ...this.getBaseConfig(propName, propInfo, false),
      isArray: false,
      items,
      required: isRequired && propInfo.types.every((type) => type !== 'null'),
      nullable: propInfo.types.includes('null')
    };
  }

  private createSimpleField(
    propName: string,
    propInfo: PropertyInformation,
    isRequired: boolean,
    isArray: boolean = false
  ): FormBuilderItem {
    const type = this.determineFieldType(propInfo);
    let values: string[] | undefined = undefined;
    let allowCustomValues: boolean | undefined = undefined;
    let customValueConfig: CustomValueConfig | undefined = undefined;

    if (type === FormBuilderFieldType.AUTO_COMPLETE) {
      if (propInfo.data?.['x-reference']) {
        values = [];
        allowCustomValues = false;
      } else if (Array.isArray(propInfo.data?.['items']?.['enum'])) {
        values = propInfo.data['items']['enum'].map((val) => String(val));
        allowCustomValues = false;
      } else if (typeof propInfo.data?.['items'] === 'object') {
        if (this.hasOneOfOrAnyOf(propInfo.data?.['items'])) {
          values = [];

          const choices =
            propInfo.data?.['items']?.['oneOf'] ||
            propInfo.data?.['items']?.['anyOf'] ||
            [];

          for (const choice of choices) {
            if (Array.isArray(choice.enum)) {
              for (const val of choice.enum) {
                values.push(String(val));
              }
            } else if (typeof choice === 'object' && choice.const !== undefined) {
              values.push(String(choice.const));
            } else if (typeof choice === 'object' && choice && choice.type === 'object') {
              // This is the custom value schema (e.g., {Other: string})
              // Check if it has properties and required fields (typical pattern for custom values)
              if (choice.properties && Object.keys(choice.properties).length > 0) {
                allowCustomValues = true;
                const wrapperProperty = Object.keys(choice.properties)[0];
                customValueConfig = {
                  strategy: CustomValueStrategy.WRAP_INDIVIDUAL,
                  wrapperProperty
                };
              }
            }
          }
        } else {
          // Tags-like pattern: simple array of strings without enum constraints
          const itemsData = propInfo.data['items'];
          const itemTypes = Array.isArray(itemsData.type)
            ? itemsData.type
            : [itemsData.type];
          const isSimpleStringType =
            itemTypes.includes('string') && !Array.isArray(itemsData.enum);

          if (isSimpleStringType) {
            values = [];
            allowCustomValues = true;
            customValueConfig = {
              strategy: CustomValueStrategy.APPEND_AS_IS
            };
          }
        }
      }
    }

    return {
      ...this.getBaseConfig(propName, propInfo, isRequired),
      type,
      isArray,
      ...(type === FormBuilderFieldType.DATE && {
        needTime: propInfo.formats.includes('partial-date-time')
      }),
      pattern: propInfo.data?.['pattern'],
      maxLength: propInfo.data?.['maxLength'],
      minLength: propInfo.data?.['minLength'],
      min: propInfo.data?.['minimum'],
      max: propInfo.data?.['maximum'],
      options: values,
      allowCustomValues: allowCustomValues,
      customValueConfig: customValueConfig,
      default: propInfo.data?.['default'] ?? undefined,
      emptyArrayIsNull:
        isArray &&
        this.isNullable(propInfo) &&
        ((propInfo.data?.['minItems'] ?? 0) >= 1 || NULL_WHEN_EMPTIED_LISTS.has(propName))
          ? true
          : undefined,
      entityType:
        (propInfo.data?.['x-reference'] ?? propInfo.data?.['items']?.['x-reference'])
          ? toScreamingSnakeCase(
              (propInfo.data?.['x-reference'] ??
                propInfo.data?.['items']?.['x-reference']) as string
            )
          : undefined
    } as FormBuilderItem;
  }

  private determineFieldType(propInfo: PropertyInformation): FormBuilderFieldType {
    const data = propInfo.data;

    if (data?.['pattern']?.includes('P(') && data?.['x-ref-type']?.includes('Duration')) {
      return FormBuilderFieldType.DURATION;
    }
    if (propInfo.formats.includes('partial-time')) {
      return FormBuilderFieldType.TIME;
    }
    // `date` is a day without a time part, so it maps to the same DATE field as
    // `partial-date-time`; `needTime` (see createSimpleField) then picks the
    // date-only picker over the datetime one.
    if (
      propInfo.formats.includes('partial-date-time') ||
      propInfo.formats.includes('date')
    ) {
      return FormBuilderFieldType.DATE;
    }
    if (data?.['x-reference']) {
      return FormBuilderFieldType.ENTITY_AUTO_COMPLETE;
    }

    const mainType = getJsonTypeOfTypesList(propInfo.types);
    switch (mainType) {
      case 'string':
        return FormBuilderFieldType.STRING;
      case 'number':
      case 'integer':
        return FormBuilderFieldType.NUMBER;
      case 'boolean':
        return FormBuilderFieldType.BOOLEAN;
      case 'array':
        if (data?.['items']?.['x-reference']) {
          return FormBuilderFieldType.ENTITY_AUTO_COMPLETE;
        }
        if (data?.['items']?.['format'] === 'date') {
          return FormBuilderFieldType.DATE;
        }
        return FormBuilderFieldType.AUTO_COMPLETE;
      default:
        return FormBuilderFieldType.STRING;
    }
  }

  transformerDisplayLabel(): string {
    return this.transformerInfo.transformer.name;
  }

  colsInOrder(): string[] {
    return this.formBuilderSchema()
      .items.sort((a, b) => a.fieldOrder - b.fieldOrder)
      .map((item) => toSnakeCase(item.id))
      .filter((prop) => prop !== 'custom_data');
  }

  /**
   * The entity's external-reference columns: fields whose value is another
   * entity's uid, paired with the referenced entity type (screaming-snake, as
   * `x-reference` carries it). Built from `formBuilderSchema(true)` — the
   * `ignoreIdentifier` flag drops the entity's own `uid`, and its own `name` is
   * never an `ENTITY_AUTO_COMPLETE` field, so both identity columns are excluded.
   */
  referenceColumns(): { field: string; entityType: string }[] {
    const refs: { field: string; entityType: string }[] = [];
    for (const item of this.formBuilderSchema(true).items) {
      if (
        'type' in item &&
        item.type === FormBuilderFieldType.ENTITY_AUTO_COMPLETE &&
        item.entityType
      ) {
        refs.push({ field: toSnakeCase(item.id), entityType: item.entityType });
      }
    }
    return refs;
  }

  /**
   * The entity's duration columns: top-level properties holding an ISO 8601
   * duration. Read straight off the schema rather than off `formBuilderSchema`,
   * whose DURATION field type only covers durations reached through a `$ref`,
   * while the data layer inlines the duration pattern on some properties.
   */
  durationColumns(): string[] {
    const properties = this.rootSchema().properties;
    if (isNil(properties)) {
      return [];
    }
    return Object.keys(properties).filter((prop) =>
      isDurationProperty(resolveAllPropertyInformation(properties[prop], this.rootSchema()))
    );
  }
}
